/**
 * Hook for resolving a set of entity IDs to their CrmObjects.
 *
 * Store-first: reads from the centralized CRM object store via
 * `useStoreObjectsMap` and only fires a network fetch (through `ensureEntity`,
 * the 50ms batcher) when an id isn't already hydrated. WebSocket updates
 * keep returned objects fresh automatically — the store is a live
 * subscription.
 *
 * This is the id-only equivalent of `useAssociations`. `useAssociations`
 * has historically been the only entry point to this functionality but its
 * input shape is association-specific (`{ association_type_name,
 * target_entity_id }[]`), which forced callers that only have ids to
 * synthesize fake association descriptors. This hook strips that
 * association_type_name baggage so callers express what they actually
 * have: a list of ids.
 *
 * Per-id loading is tracked separately from "fetch finished without
 * resolving" — when a target's most recent fetch attempt settled without an
 * object, `loading` flips false and `object` stays null, so a
 * permanently-broken reference doesn't spin forever. `missing` then says
 * WHY: true when the server answered and omitted the id (deleted, bad
 * reference, or hidden from this user), false when the request itself
 * failed and nothing is known. Callers that act on absence (rather than
 * merely stop waiting) must check `missing`, not `!loading && !object`.
 */

import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';

import {
  ensureEntity,
  getFailureVersion,
  hasFailedFetch,
  hasMissingEntity,
  subscribeToFetchFailures,
} from '@/lib/crm/object-cards/entityFetchBatcher';
import { hasCompleteObject, useStoreObjectsMap } from '@/lib/crm/store';
import type { CrmObject } from '@/types/crm';

export interface ResolvedStoreObject {
  /** The entity id passed in by the caller. */
  entityId: string;
  /**
   * Resolved CRM object from the store, or null when missing / incomplete /
   * still loading. Only *complete* store entries (`meta.isComplete`) resolve
   * here — a summary/WS-only entry is treated as not-yet-resolved so callers
   * never read partial data (missing properties, stale associations).
   */
  object: CrmObject | null;
  /**
   * True when the target isn't yet resolved to a complete store entry and its
   * fetch is in flight. A present-but-incomplete (summary) entry counts as
   * loading until `ensureEntity` upgrades it to complete or its fetch fails.
   */
  loading: boolean;
  /**
   * True when the most recent fetch SUCCEEDED and the server omitted this id:
   * the record does not exist for this user (deleted, bad reference, or
   * hidden by access control). Always false while `loading`, and false when
   * the request itself failed — a transport error says nothing about the
   * record, so callers must not treat it as absence.
   */
  missing: boolean;
}

export interface UseEnsuredStoreObjectsOptions {
  /**
   * When `false`, the hook only reads what the store already has and does
   * NOT trigger a batched fetch for missing targets. Use this when eager
   * hydration would be prohibitively expensive (e.g. a list with thousands
   * of ids). Defaults to `true`.
   */
  ensure?: boolean;
}

export interface UseEnsuredStoreObjectsResult {
  /** Per-id resolution result, in the same order as the input. */
  objects: ResolvedStoreObject[];
  /** True when at least one id is still being fetched. */
  isLoading: boolean;
  /** True when at least one id's most recent fetch attempt failed. */
  hasPartialError: boolean;
  /** Set when EVERY id's most recent fetch attempt failed. */
  error: Error | null;
  /** Re-queue every missing id through the batcher. */
  refetch: () => Promise<void>;
}

/**
 * Resolve a set of entity ids to their CrmObjects, reading from the store
 * and triggering a batched fetch only for missing entries.
 */
export function useEnsuredStoreObjects(
  orgId: number | undefined,
  entityIds: string[],
  options?: UseEnsuredStoreObjectsOptions
): UseEnsuredStoreObjectsResult {
  const ensure = options?.ensure ?? true;

  // Stable, sorted list of non-empty target ids. Sorting means caller
  // re-orderings don't churn dependent hooks; sub-component callers
  // typically don't care about input order anyway since the result is
  // re-aligned to the original `entityIds` order below.
  const targetIds = useMemo(() => {
    const ids = entityIds.filter(Boolean);
    return ids.slice().sort();
  }, [entityIds]);

  // Trigger a batched fetch for any targets not already complete in the
  // store. `ensureEntity` is idempotent — no-op when the store has a
  // complete entry or the same id is already in flight.
  useEffect(() => {
    if (!ensure || !orgId || orgId <= 0) {
      return;
    }
    for (const id of targetIds) {
      ensureEntity(orgId, id);
    }
  }, [orgId, targetIds, ensure]);

  // Live store read — re-renders when any watched target arrives / updates.
  // Keyed by the REQUESTED id (not the canonical post-merge `entity_id`),
  // so merged-target lookups still match the id the caller passed in.
  const storeObjectsById = useStoreObjectsMap(targetIds);

  // Subscribe to the batcher's failure notifications so the hook re-renders
  // when a target fails (or a retry clears the flag).
  const failureVersion = useSyncExternalStore(
    subscribeToFetchFailures,
    getFailureVersion,
    getFailureVersion
  );

  // Re-align resolution results to the caller's original input order so
  // ordering matches what they passed in (sorted only for memo stability).
  const objects = useMemo<ResolvedStoreObject[]>(
    () =>
      entityIds.map(entityId => {
        // Empty ids are excluded from `targetIds` and never fetched.
        // Surface them as resolved-but-missing so callers don't see an
        // infinite loading state.
        if (!entityId) {
          return { entityId, object: null, loading: false, missing: false };
        }
        const stored = storeObjectsById.get(entityId) ?? null;
        // Only a *complete* store entry resolves. A summary/WS-only entry
        // (`isComplete: false`) lacks the full property/association set, so
        // treating it as resolved would surface partial data (and let a save
        // overwrite from an incomplete baseline). `ensureEntity` fetches
        // incomplete entries to upgrade them, so this window is transient.
        const complete = stored !== null && hasCompleteObject(entityId);
        const object = complete ? stored : null;
        const failed = hasFailedFetch(entityId);
        // "Still loading" means the fetch hasn't settled into a complete
        // entry. A fetch that settled WITHOUT resolving (failed, 404, deleted)
        // is a terminal state and must not show as loading — this is what
        // keeps a permanently-incomplete/broken reference from spinning
        // forever.
        const loading = ensure && !complete && !failed;
        // Only a settled, object-less entry can be missing — and only when
        // the server actually answered (see `hasMissingEntity`).
        const missing = !complete && !loading && hasMissingEntity(entityId);
        return { entityId, object, loading, missing };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- failureVersion bumps recompute
    [entityIds, storeObjectsById, ensure, failureVersion]
  );

  const isLoading = objects.some(o => o.loading);

  const hasPartialError = useMemo(
    () => objects.some(o => o.object === null && hasFailedFetch(o.entityId)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- failureVersion bumps recompute
    [objects, failureVersion]
  );

  const error = useMemo<Error | null>(() => {
    if (objects.length === 0) {
      return null;
    }
    const allFailed = objects.every(
      o => o.object === null && hasFailedFetch(o.entityId)
    );
    return allFailed ? new Error('Failed to load objects') : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- failureVersion bumps recompute
  }, [objects, failureVersion]);

  const refetch = useCallback(() => {
    if (!orgId || orgId <= 0) {
      return Promise.resolve();
    }
    for (const id of targetIds) {
      ensureEntity(orgId, id);
    }
    return Promise.resolve();
  }, [orgId, targetIds]);

  return {
    objects,
    isLoading,
    hasPartialError,
    error,
    refetch,
  };
}
